/**
 * Token decoding + storage helpers.
 *
 * - Tokens themselves NEVER touch `localStorage`/`sessionStorage` (per
 *   readiness Q-2 IAM contract — XSS-exfiltration mitigation). They live
 *   in module-scoped state inside `auth/state.ts` and the React context.
 * - `sessionStorage` is used ONLY for the PKCE `code_verifier` + `state`
 *   during the redirect round-trip; cleared immediately after exchange.
 */

import { base64UrlEncode } from "@/auth/pkce";

const PKCE_VERIFIER_KEY = "sr.onboarding.pkce.verifier";
const PKCE_STATE_KEY = "sr.onboarding.pkce.state";
const PKCE_NEXT_KEY = "sr.onboarding.pkce.next";

export interface TokenSet {
  /** Raw access JWT — sent as `Authorization: Bearer <access_token>`. */
  accessToken: string;
  /** Unix timestamp (seconds) at which `accessToken.exp` fires. */
  expiresAtSec: number;
  /**
   * Raw OIDC ID token from the code exchange, kept in memory beside the access token.
   * Used only as `id_token_hint` on IAM end-session, which is what lets IAM end its own
   * session on sign out (docs#15 note 16577). Absent when IAM sent none.
   */
  idToken?: string;
  /**
   * When this token set arrived, Unix seconds. Used to bound the refresh lead by the
   * token's lifetime when the JWT carries no `iat`. Absent on hand-built token sets.
   */
  obtainedAtSec?: number;
  // NOTE: no refresh token here. It never reaches JS — the BFF keeps it in an
  // httpOnly cookie (ADR-0041); silent refresh POSTs to /auth/refresh with
  // `credentials: "include"` and the browser attaches the cookie.
}

export interface DecodedClaims {
  sub: string;
  email?: string;
  name?: string;
  exp?: number;
  iat?: number;
  permissions: ReadonlyArray<string>;
  scopes: ReadonlyArray<string>;
  /** ADR-0022 role, informational only. The portal gates on the server-resolved
   * `clients:manage` permission, not on this claim (see `RequireClientAdmin`). */
  role?: string;
  /**
   * The `roles` list real IAM user tokens carry since iam-service !340, in kebab case
   * (`["client-admin", "author"]`), with no singular `role`. Informational, like
   * `role`: nothing in the web app gates on either. `[]` when the claim is absent.
   */
  roles: ReadonlyArray<string>;
  /**
   * The tenant this principal belongs to (ADR-0021/0033). REQUIRED for
   * a portal admin per ADR-0022, and the portal refuses to render without it, and
   * the backend derives the org from this claim rather than any request input.
   */
  organization_unit_id?: string;
}

/** Decode a JWT payload without signature validation (server validates). */
export function decodeJwtPayload(jwt: string): DecodedClaims {
  const parts = jwt.split(".");
  if (parts.length !== 3) {
    throw new Error("Malformed JWT: expected three dot-separated segments.");
  }
  const payload = parts[1] as string;
  // base64url → base64 → atob → JSON. Pad with `=` to a multiple of 4.
  const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  let json: string;
  try {
    // atob yields a Latin-1 byte string; decode the bytes as UTF-8 so a multibyte
    // claim (e.g. an accented or non-Latin display name) isn't mangled to mojibake.
    const bytes = Uint8Array.from(atob(padded), (ch) => ch.charCodeAt(0));
    json = new TextDecoder().decode(bytes);
  } catch {
    throw new Error("Malformed JWT: payload is not valid base64url.");
  }
  let raw: Record<string, unknown>;
  try {
    raw = JSON.parse(json) as Record<string, unknown>;
  } catch {
    throw new Error("Malformed JWT: payload is not valid JSON.");
  }
  // ADR-0026: `permissions` is the user-RBAC claim. IAM emits it as an
  // array; tolerate the legacy space-delimited string shape too.
  const permissions = normalizeStringList(raw["permissions"]);
  // OAuth2 `scope` is a single space-separated string; some IdPs emit
  // an array. Either is fine — flatten both shapes.
  const scopes = normalizeStringList(raw["scope"] ?? raw["scopes"]);
  const sub = typeof raw["sub"] === "string" ? raw["sub"] : "";
  if (sub === "") {
    throw new Error("Malformed JWT: missing `sub` claim.");
  }
  const claims: DecodedClaims = {
    sub,
    permissions,
    scopes,
    // A JSON list as real IAM sends it. A one-role user may arrive as a bare string,
    // which is how OpenIddict serialises a single-valued claim.
    roles: normalizeStringList(raw["roles"]),
  };
  if (typeof raw["email"] === "string") claims.email = raw["email"];
  if (typeof raw["name"] === "string") claims.name = raw["name"];
  if (typeof raw["exp"] === "number") claims.exp = raw["exp"];
  if (typeof raw["iat"] === "number") claims.iat = raw["iat"];
  if (typeof raw["role"] === "string") claims.role = raw["role"];
  if (typeof raw["organization_unit_id"] === "string") {
    claims.organization_unit_id = raw["organization_unit_id"];
  }
  return claims;
}

function normalizeStringList(raw: unknown): ReadonlyArray<string> {
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === "string");
  }
  if (typeof raw === "string" && raw.length > 0) {
    return raw.split(/\s+/).filter((s) => s.length > 0);
  }
  return [];
}

/** Construct a `TokenSet` from the BFF's token response body.
 *
 * The body carries only the access token + metadata — the refresh token was
 * stripped into an httpOnly cookie by the BFF and is intentionally absent. */
export function tokenSetFromResponse(body: {
  access_token: string;
  expires_in?: number | null;
  id_token?: string | null;
}): TokenSet {
  if (typeof body.access_token !== "string" || body.access_token.length === 0) {
    throw new Error("Token response missing `access_token`.");
  }
  // Prefer the JWT's own `exp` if present; otherwise lean on `expires_in`
  // relative to wall clock at receipt. Both are advisory — refresh fires
  // 60s before whichever we settle on.
  let expiresAtSec: number;
  try {
    const decoded = decodeJwtPayload(body.access_token);
    if (typeof decoded.exp === "number") {
      expiresAtSec = decoded.exp;
    } else if (typeof body.expires_in === "number") {
      expiresAtSec = Math.floor(Date.now() / 1000) + body.expires_in;
    } else {
      expiresAtSec = Math.floor(Date.now() / 1000) + 900; // 15 min default
    }
  } catch {
    expiresAtSec =
      typeof body.expires_in === "number"
        ? Math.floor(Date.now() / 1000) + body.expires_in
        : Math.floor(Date.now() / 1000) + 900;
  }
  const tokens: TokenSet = {
    accessToken: body.access_token,
    expiresAtSec,
    obtainedAtSec: Math.floor(Date.now() / 1000),
  };
  if (typeof body.id_token === "string" && body.id_token.length > 0) {
    tokens.idToken = body.id_token;
  }
  return tokens;
}

// ----- PKCE round-trip storage (sessionStorage; cleared post-exchange) -----

export function storePkceVerifier(verifier: string, state: string, next: string): void {
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);
  sessionStorage.setItem(PKCE_NEXT_KEY, next);
}

export function readPkceVerifier(): {
  verifier: string | null;
  state: string | null;
  next: string;
} {
  return {
    verifier: sessionStorage.getItem(PKCE_VERIFIER_KEY),
    state: sessionStorage.getItem(PKCE_STATE_KEY),
    next: sessionStorage.getItem(PKCE_NEXT_KEY) ?? "/",
  };
}

export function clearPkceVerifier(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  sessionStorage.removeItem(PKCE_NEXT_KEY);
}

// Re-export for convenience.
export { base64UrlEncode };

// ----- Signed-out marker (sessionStorage, no token, one flag) -----
//
// Set just before the browser leaves for IAM end-session and read when it comes back.
// IAM returns to the registered origin, which is a protected route, and the guard would
// otherwise send a signed-out user straight into a new login. With the marker the guard
// shows the signed-out page instead, and the user chooses to sign in again (docs#15
// note 16577). Cleared when a new login starts.

const SIGNED_OUT_KEY = "sr.onboarding.signed_out";

export function markSignedOut(): void {
  sessionStorage.setItem(SIGNED_OUT_KEY, "1");
}

export function wasSignedOut(): boolean {
  return sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
}

export function clearSignedOut(): void {
  sessionStorage.removeItem(SIGNED_OUT_KEY);
}
